import { Component, OnInit, OnDestroy, HostListener, signal, ElementRef, ViewChild, effect, computed } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { LockScreenService } from './lock-screen.service';
import { SettingsService } from '../settings/settings.service';
import { AuthService, User } from '../services/auth.services';

@Component({
  selector: 'app-lock-screen',
  templateUrl: './lock-screen.component.html',
  styleUrls: ['./lock-screen.component.scss'],
  standalone: false
})
export class LockScreenComponent implements OnInit, OnDestroy {
  @ViewChild('sliderTrack') sliderTrack!: ElementRef<HTMLDivElement>;

  readonly isLocked = signal(false);
  private currentRoute = signal('');
  currentTime = signal('');
  currentDate = signal('');
  userName = signal('User');
  userAvatar = signal('');

  // Computed signal to show lock screen only when locked AND on /home route
  readonly shouldShowLockScreen = computed(() => {
    return this.isLocked() && this.currentRoute().startsWith('/home');
  });

  // Drag state
  isDragging = signal(false);
  dragOffset = signal(0);
  dragProgress = signal(0);
  isUnlocking = signal(false);

  private startX = 0;
  private trackWidth = 0;
  private readonly UNLOCK_THRESHOLD = 0.8; // 80% of track to unlock

  private idleTimeout: any;
  private clockInterval: any;
  private routerSubscription?: Subscription;
  private readonly destroy$ = new Subject<void>();

  constructor(
    public auth: AuthService,
    private readonly lockScreenService: LockScreenService,
    private readonly settingsService: SettingsService,
    private readonly router: Router
  ) {
    // React to service lock state changes
    effect(() => {
      this.isLocked.set(this.lockScreenService.isLocked());
    });

    // Track route changes
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.currentRoute.set(event.url);
      });

    // Set initial route
    this.currentRoute.set(this.router.url);
  }

  ngOnInit(): void {
    this.startIdleTimer();
    this.updateClock();
    this.clockInterval = setInterval(() => this.updateClock(), 1000);

    // Subscribe to user so lock screen updates when user is set (e.g. after login)
    this.auth.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe((userProfile: any) => {
        const user: User | null = userProfile?.profile ? userProfile.profile : userProfile;
        if (user && typeof user === 'object') {
          this.userName.set(user.name || user.email || 'User');
          this.userAvatar.set(user.picture || user.photoURL || 'data:image/gif;base64,R0lGODlhAQABAAAAACwAAAAAAQABAAA=');
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearIdleTimer();
    if (this.clockInterval) {
      clearInterval(this.clockInterval);
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  @HostListener('window:mousemove')
  @HostListener('window:mousedown')
  @HostListener('window:keypress')
  @HostListener('window:touchstart')
  @HostListener('window:scroll')
  onUserActivity(): void {
    if (!this.isLocked()) {
      this.resetIdleTimer();
    }
  }

  private startIdleTimer(): void {
    const privacySettings = this.settingsService.privacySettings();

    // Don't start timer if auto-lock is disabled
    if (!privacySettings.autoLockEnabled) {
      return;
    }

    const timeoutMs = this.settingsService.getAutoLockTimeoutMs();

    // Don't lock if timeout is set to "Never" (-1)
    if (timeoutMs === -1) {
      return;
    }

    this.idleTimeout = setTimeout(() => {
      this.lockScreen();
    }, timeoutMs);
  }

  private resetIdleTimer(): void {
    this.clearIdleTimer();
    this.startIdleTimer();
  }

  private clearIdleTimer(): void {
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
    }
  }

  private lockScreen(): void {
    this.lockScreenService.lock();
    this.updateClock();
  }

  unlock(): void {
    this.lockScreenService.unlock();
    this.resetIdleTimer();
    this.resetDragState();
  }

  // Drag handling
  onDragStart(event: MouseEvent | TouchEvent | PointerEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.isDragging.set(true);
    this.startX = this.getClientX(event);

    if (this.sliderTrack) {
      this.trackWidth = this.sliderTrack.nativeElement.offsetWidth - 60; // 60 = thumb width
    }
  }

  @HostListener('window:mousemove', ['$event'])
  @HostListener('window:touchmove', ['$event'])
  @HostListener('window:pointermove', ['$event'])
  onDragMove(event: MouseEvent | TouchEvent | PointerEvent): void {
    if (!this.isDragging()) return;

    const currentX = this.getClientX(event);
    let offset = currentX - this.startX;

    // Clamp offset between 0 and max track width
    offset = Math.max(0, Math.min(offset, this.trackWidth));

    this.dragOffset.set(offset);

    // Calculate progress (0 to 1)
    const progress = offset / this.trackWidth;
    this.dragProgress.set(progress);

    // Check if past unlock threshold
    this.isUnlocking.set(progress >= this.UNLOCK_THRESHOLD);
  }

  @HostListener('window:mouseup')
  @HostListener('window:touchend')
  @HostListener('window:pointerup')
  @HostListener('window:pointercancel')
  onDragEnd(): void {
    if (!this.isDragging()) return;

    if (this.isUnlocking()) {
      // Unlock!
      this.unlock();
    } else {
      // Spring back animation
      this.animateSpringBack();
    }

    this.isDragging.set(false);
  }

  private getClientX(event: MouseEvent | TouchEvent | PointerEvent): number {
    // Handle PointerEvent (works better in Firefox)
    if ('pointerId' in event) {
      return (event as PointerEvent).clientX;
    }

    // Handle TouchEvent
    if ('touches' in event) {
      const touchEvent = event as TouchEvent;
      // Use touches[0] for active touches, changedTouches[0] for ended touches
      if (touchEvent.touches && touchEvent.touches.length > 0) {
        return touchEvent.touches[0].clientX;
      }
      if (touchEvent.changedTouches && touchEvent.changedTouches.length > 0) {
        return touchEvent.changedTouches[0].clientX;
      }
      return 0;
    }

    // Handle MouseEvent
    return (event as MouseEvent).clientX;
  }

  private animateSpringBack(): void {
    const currentOffset = this.dragOffset();
    const duration = 300;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const newOffset = currentOffset * (1 - eased);

      this.dragOffset.set(newOffset);
      this.dragProgress.set(newOffset / this.trackWidth);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.resetDragState();
      }
    };

    requestAnimationFrame(animate);
  }

  private resetDragState(): void {
    this.dragOffset.set(0);
    this.dragProgress.set(0);
    this.isUnlocking.set(false);
  }

  private updateClock(): void {
    const now = new Date();

    // Format time as HH:MM
    this.currentTime.set(now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }));

    // Format date as Day, Month Date
    this.currentDate.set(now.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    }));
  }

  // Manual lock function (can be called from elsewhere)
  manualLock(): void {
    this.clearIdleTimer();
    this.lockScreen();
  }
}

